import React, { Suspense, useEffect, useState, lazy } from 'react';
import { Route, Routes, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { userStore } from './stores';
import { fetchPermissions, fetchBusinessRules } from './utils/calls';
import GetApolloClient from './Apollo';
import { ApolloProvider } from '@apollo/client';
import { StoreProvider } from './store/Store';
import Login from './containers/Login';
import OIDCRedirect from './containers/OIDCRedirect';
import Unauthorized from './containers/Unauthorized';
import Header from './components/Header';
import { checkForRequiredPermissions } from './utils/helpers/permissionsHelpers';
import './app.scss';
import ArchiveSearch from './components/ArchiveSearchComponents/ArchiveSearch';

const App = () => {
  const MapSearch = lazy(() => import('./containers/MapSearch'));
  const BaseballCards = lazy(() => import('./containers/BaseballCards/BaseballCards'));
  const BreadcrumbsView = lazy(() => import('./containers/Breadcrumbs/BreadcrumbsView'));
  const PageNotFound = lazy(() => import('./components/PageNotFound'));
  const TicketDetailView = lazy(() => import('./components/TicketDetailComponents'));
  const TicketDetail = lazy(() => import('./containers/TicketDetail'));
  const TicketTypeSummary = lazy(() => import('./containers/TicketTypeSummary'));
  const Lookup = lazy(() => import('./Lookup/containers/Lookup'));
  const DevAppSelection = lazy(() => import('./containers/DevAppSelection'));
  const SelfService = lazy(() => import('./containers/SelfService'));
  const OQPortal = lazy(() => import('./containers/OQPortal'));
  const Dashboard = lazy(() => import('./containers/Dashboard'));
  const TicketsList = lazy(() => import('./containers/TicketsList'));
  const Notifications = lazy(() => import('./containers/Notifications'));
  const checkForHost = host => window.location.host.split('.')[0] === host;
  const isLocalEnv = process.env.REACT_APP_LOCAL_ENV === 'true';
  const isBreadcrumbsEnabled = process.env.REACT_APP_BREADCRUMBS === 'true';
  const isSelfServiceEnabled = process.env.REACT_APP_SELF_SERVICE_ENABLED === 'true';
  const isOQPortalEnabled = process.env.REACT_APP_OQ_PORTAL_ENABLED === 'true';
  const isSupervisorPagesEnabled = process.env.REACT_APP_SUPERVISOR_PAGES_ENABLED === 'true';
  const isOnCallPagesEnabled = process.env.REACT_APP_ON_CALL_PAGES_ENABLED === 'true';

  const [loading, setLoading] = useState(false);
  const setPermissionsData = data => {
    userStore.setAccess(data);
    userStore.setPermissions(data.permissions);
    userStore.setRoles(data.roles);
    setLoading(false);
  };

  let auth = useAuth();
  const access = userStore.getAccess;
  if (auth.user && !userStore.getAccessToken) {
    userStore.setAccessToken(auth.user.access_token);
    userStore.setIdToken(auth.user.id_token);
    const lanId = auth.user.profile.email
      ? auth.user.profile.email.split('@')[0] || 'NO LANID'
      : auth.user.profile.sub;
    userStore.setLanId(lanId);
    if (process.env.REACT_APP_RBA_ENABLED === 'true') {
      if (auth.user && (!access.roles || !access.permissions) && !loading) {
        setLoading(true);
        if (process.env.REACT_APP_RBA_ENABLED === 'true') {
          // check permissions and grant/deny access to this app
          console.log('FETCHING PERMISSIONS: IN PROGRESS');
          fetchPermissions(auth.user.access_token).then(data => {
            console.log('FETCHING PERMISSIONS: COMPLETED', data);
            if (!data) {
              data = {
                roles: [],
                permissions: [],
              };
              fetchBusinessRules(auth.user.access_token).then(businessRules => {
                console.log('FETCHING BUSINESS RULES: COMPLETED', businessRules);
                if (businessRules?.webConfig) {
                  data.roles = businessRules.webConfig?.defaultRoles;
                  data.permissions = businessRules.webConfig?.defaultPermissions;
                  setPermissionsData(data);
                }
              });
            } else {
              setPermissionsData(data);
            }
          });
        }
      }
    }
  }
  useEffect(() => {
    // the `return` is important - addAccessTokenExpiring() returns a cleanup function
    return auth.events.addAccessTokenExpiring(() => {
      if (
        alert("You're about to be signed out due to inactivity. Press continue to stay signed in.")
      ) {
        auth.signinSilent();
      }
    });
  }, [auth.events, auth.signinSilent]);

  if (auth.isLoading) {
    return <div>Loading...</div>;
  }
  if (auth.error) {
    return <div>Error... {auth.error.message}</div>;
  }

  const RequireAuth = ({ protectedElement, showHeader = true }) => {
    const location = useLocation();

    if (!auth.isAuthenticated) {
      const originalPath = window.location.pathname;
      sessionStorage.setItem('originalPath', originalPath);
      return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // Components that bypass permission checks
    const componentsExcludedFromPermissions = ['Lookup', 'DevAppSelection', 'PageNotFound'];
    if (componentsExcludedFromPermissions.includes(protectedElement.key)) {
      return protectedElement;
    }

    // Map component keys to permission names
    const permissionMap = {
      Notifications: 'SUPERVISOR_ONCALL',
      BreadcrumbsView: 'BREADCRUMBS',
      MapSearch: 'MAPSEARCH',
      Dashboard: 'SUPERVISOR',
      TicketsList: 'SUPERVISOR',
      BaseballCards: 'BREADCRUMBS',
      TicketTypeSummary: 'SUPERVISOR_ONCALL',
      Archive: 'SUPERVISOR',
      SelfService: 'SUPERVISOR_ONCALL',
      OQPortal: 'OQ_VIEW',
      TicketDetailView: 'SUPERVISOR',
    };

    const permissionToCheck = permissionMap[protectedElement.key] || 'MAPSEARCH';
    // Using userStore permissions directly via the function's fallback
    const hasPermission = checkForRequiredPermissions(permissionToCheck);

    if (!hasPermission) {
      return <Unauthorized />;
    }

    return showHeader ? (
      <>
        <Header />
        {protectedElement}
      </>
    ) : (
      protectedElement
    );
  };

  return (
    <div className="app" id="App">
      <div className={'source-version'}>{process.env.REACT_APP_VERSION.substring(0, 7)}</div>
      <Suspense fallback={<div className="view-loading">Loading...</div>}>
        <ApolloProvider client={GetApolloClient(userStore.access_token)}>
          <StoreProvider>
            <Routes>
              <Route path="*" element={<PageNotFound />} />
              <Route path="/login" element={<Login />} />
              <Route path="/redirect" element={<OIDCRedirect />} />

              {/* Rendering when on lookup subdomain */}
              {checkForHost(process.env.REACT_APP_LOOKUP_SUBDOMAIN) && (
                <Route
                  path="/"
                  element={<RequireAuth protectedElement={<Lookup key={'Lookup'} />} />}
                />
              )}

              {/* Rendering when on locate subdomain */}
              {checkForHost(process.env.REACT_APP_MAP_SUBDOMAIN) && (
                <>
                  {/* Root path redirects to mapsearch */}
                  <Route path="/" element={<Navigate to="/mapsearch" replace />} />

                  <Route
                    path="/mapsearch"
                    element={<RequireAuth protectedElement={<MapSearch key={'MapSearch'} />} />}
                  />

                  <Route
                    path="/ticketDetails/:id"
                    element={
                      <RequireAuth
                        protectedElement={<TicketDetailView key={'TicketDetailView'} />}
                      />
                    }
                  />

                  <Route
                    path="/ticketDetail/:id"
                    element={
                      <RequireAuth protectedElement={<TicketDetail key={'TicketDetail'} />} />
                    }
                  />

                  {isBreadcrumbsEnabled && (
                    <Route
                      path="/breadcrumbs"
                      element={
                        <RequireAuth
                          protectedElement={<BreadcrumbsView key={'BreadcrumbsView'} />}
                        />
                      }
                    />
                  )}

                  <Route
                    path="/baseballcards"
                    element={
                      <RequireAuth protectedElement={<BaseballCards key={'BaseballCards'} />} />
                    }
                  />

                  {isSelfServiceEnabled && (
                    <Route
                      path="/selfservice"
                      element={
                        <RequireAuth protectedElement={<SelfService key={'SelfService'} />} />
                      }
                    />
                  )}

                  {isOQPortalEnabled && (
                    <Route
                      path="/oqportal"
                      element={<RequireAuth protectedElement={<OQPortal key={'OQPortal'} />} />}
                    />
                  )}

                  {isSupervisorPagesEnabled && (
                    <>
                      <Route
                        path="/dashboard"
                        element={<RequireAuth protectedElement={<Dashboard key={'Dashboard'} />} />}
                      />

                      <Route
                        path="/ticketslist"
                        element={
                          <RequireAuth protectedElement={<TicketsList key={'TicketsList'} />} />
                        }
                      />

                      <Route
                        path="/archive"
                        element={
                          <RequireAuth protectedElement={<ArchiveSearch key={'Archive'} />} />
                        }
                      />
                    </>
                  )}

                  <Route
                    path="/tickettypesummary"
                    element={
                      <RequireAuth
                        protectedElement={<TicketTypeSummary key={'TicketTypeSummary'} />}
                      />
                    }
                  />

                  {isOnCallPagesEnabled && (
                    <Route
                      path="/notifications"
                      element={
                        <RequireAuth protectedElement={<Notifications key={'Notifications'} />} />
                      }
                    />
                  )}
                </>
              )}
              {/* Rendering when on localhost */}
              {isLocalEnv && (
                <>
                  <Route
                    path="/"
                    element={
                      <RequireAuth protectedElement={<DevAppSelection key={'DevAppSelection'} />} />
                    }
                  />

                  {/* MapSearch with clean URL */}
                  <Route
                    path="/mapsearch"
                    element={<RequireAuth protectedElement={<MapSearch key={'MapSearch'} />} />}
                  />

                  <Route
                    path="/breadcrumbs"
                    element={
                      <RequireAuth protectedElement={<BreadcrumbsView key={'BreadcrumbsView'} />} />
                    }
                  />

                  <Route
                    path="/ticketDetails/:id"
                    element={
                      <RequireAuth
                        protectedElement={<TicketDetailView key={'TicketDetailView'} />}
                      />
                    }
                  />

                  <Route
                    path="/ticketDetail/:id"
                    element={
                      <RequireAuth protectedElement={<TicketDetail key={'TicketDetail'} />} />
                    }
                  />

                  {isSupervisorPagesEnabled && (
                    <>
                      <Route
                        path="/dashboard"
                        element={<RequireAuth protectedElement={<Dashboard key={'Dashboard'} />} />}
                      />

                      <Route
                        path="/ticketslist"
                        element={
                          <RequireAuth protectedElement={<TicketsList key={'TicketsList'} />} />
                        }
                      />

                      <Route
                        path="/archive"
                        element={
                          <RequireAuth protectedElement={<ArchiveSearch key={'Archive'} />} />
                        }
                      />
                    </>
                  )}

                  <Route
                    path="/selfservice"
                    element={<RequireAuth protectedElement={<SelfService key={'SelfService'} />} />}
                  />

                  <Route
                    path="/oqportal"
                    element={<RequireAuth protectedElement={<OQPortal key={'OQPortal'} />} />}
                  />

                  <Route
                    path="/lookup"
                    element={<RequireAuth protectedElement={<Lookup key={'Lookup'} />} />}
                  />

                  <Route
                    path="/baseballcards"
                    element={
                      <RequireAuth protectedElement={<BaseballCards key={'BaseballCards'} />} />
                    }
                  />

                  <Route
                    path="/tickettypesummary"
                    element={
                      <RequireAuth
                        protectedElement={<TicketTypeSummary key={'TicketTypeSummary'} />}
                      />
                    }
                  />

                  {isOnCallPagesEnabled && (
                    <Route
                      path="/notifications"
                      element={
                        <RequireAuth protectedElement={<Notifications key={'Notifications'} />} />
                      }
                    />
                  )}
                </>
              )}
              <Route
                path="*"
                element={<RequireAuth protectedElement={<PageNotFound key={'PageNotFound'} />} />}
              />
            </Routes>
          </StoreProvider>
        </ApolloProvider>
      </Suspense>
    </div>
  );
};

export default App;
